import { Decimal } from 'decimal.js'
import { OrderChannel } from 'constant/OrderChannels'
import {
    DiscountAppliesTo,
    DiscountContract,
    DiscountLevel,
    DiscountMinimumRequirement,
    DiscountType,
    OrderLineStatus,
} from 'models'
import {
    DiscountContractWithCustom,
    OrderFulfilmentType,
    OrderItemViewModel,
} from 'store/models/sellModels'
import {
    appliesToCompareByItemFilterDiscount,
    itemMinReqFilterDiscount,
} from './discountFilter/discountFilter'
import generalDiscountFilter from './discountFilter/discountGeneralFilter'
import { sortDiscountTypeOfDiscount } from './discountSortting'
import { calculateDiscountAmount } from 'views/Sale/utils/orderCalculation/discountCalculation'
import {
    calculateLineSubtotal,
    sumItemAmount,
    sumLineSubtotal,
    sumTotalDiscount,
} from 'views/Sale/utils/orderCalculation/common'

export const getEntireDiscountInApplicables = (
    orderItems: OrderItemViewModel[],
    entireOrderDiscounts: DiscountContractWithCustom[],
    checkingFulfillmentType: number,
    orderChannelID: number,
) => {
    return entireOrderDiscounts.filter(
        (enOrderDiscount) =>
            !groupEntireOrderDiscountFilter(
                enOrderDiscount,
                orderItems,
                checkingFulfillmentType,
                [],
                orderChannelID,
            ),
    )
}

export const chooseBestNonCombinableDiscount = (
    lineTotal: Decimal,
    discounts: DiscountContract[],
): DiscountContract | null => {
    if (discounts.length === 0) return null

    let bestNonCombine = discounts[0]
    let bestDiscountAmount = calculateDiscountAmount(discounts[0], lineTotal)

    for (let i = 1; i < discounts.length; i++) {
        const curDiscountAmount = calculateDiscountAmount(
            discounts[i],
            lineTotal,
        )
        if (curDiscountAmount.greaterThanOrEqualTo(bestDiscountAmount)) {
            bestDiscountAmount = curDiscountAmount
            bestNonCombine = discounts[i]
        }
    }

    return bestNonCombine
}

export const entireOrderDiscountfilterByMinRequirement = (
    items: OrderItemViewModel[],
    discount: DiscountContract,
): boolean => {
    const itemWithoutVoided = items.filter((f) => {
        if (f.status !== OrderLineStatus.Voided) {
            return true
        } else {
            //if that item was voide and is removed void now
            return f.voidComp ? false : true
        }
    })
    let subTotal = sumLineSubtotal(itemWithoutVoided)
    const subDiscount = itemWithoutVoided.reduce(
        (prev, item) => prev.plus(sumTotalDiscount(item)),
        new Decimal(0),
    )
    subTotal = subTotal.minus(subDiscount) // subTotal with discount should minus sum of discount
    const itemsAmount = sumItemAmount(itemWithoutVoided)
    const { minRequirement, minRequirementValue } = discount
    if (minRequirement === DiscountMinimumRequirement.MinimumPurchaseAmount) {
        return subTotal.greaterThanOrEqualTo(minRequirementValue!!)
    } else if (
        minRequirement === DiscountMinimumRequirement.MinimumQuantityOfItems
    ) {
        return itemsAmount >= minRequirementValue!!
    }

    return true
}

export const chooseOptimalAutoDiscount = (
    automaticDiscounts: DiscountContract[],
    item: OrderItemViewModel,
): DiscountContract[] => {
    /**
     * priority to IsCombinable. Pick all of combinable discounts
     */
    if (automaticDiscounts.some((s) => s.isCombinable)) {
        automaticDiscounts = automaticDiscounts.filter((s) => s.isCombinable)
        automaticDiscounts = sortDiscountTypeOfDiscount(automaticDiscounts)
    } else {
        /**
         * No any Combinable case. Pick the best one
         */
        if (automaticDiscounts.length > 1) {
            const lineTotal = calculateLineSubtotal(item)
            const bestNonCombine = chooseBestNonCombinableDiscount(
                lineTotal,
                automaticDiscounts,
            )

            automaticDiscounts = bestNonCombine ? [bestNonCombine] : []
        }
    }

    return automaticDiscounts
}

export const applyToPriorityOfDiscounts = (
    autoEntireDiscounts: DiscountContract[],
): DiscountContract[] => {
    if (
        autoEntireDiscounts.some(
            (s) => s.appliesTo === DiscountAppliesTo.SelectedProducts,
        )
    ) {
        return autoEntireDiscounts.filter(
            (f) => f.appliesTo === DiscountAppliesTo.SelectedProducts,
        )
    }

    if (
        autoEntireDiscounts.some(
            (s) => s.appliesTo === DiscountAppliesTo.SelectedCategories,
        )
    ) {
        return autoEntireDiscounts.filter(
            (f) => f.appliesTo === DiscountAppliesTo.SelectedCategories,
        )
    }

    return autoEntireDiscounts
}

export const groupAutoEachItemDiscountFilter = (
    discount: DiscountContract,
    item: OrderItemViewModel,
    checkingFulfillmentType: number,
    excludeAutoDiscountIds: string[] = [],
    orderChannel: OrderChannel,
) => {
    return (
        generalDiscountFilter(
            discount,
            checkingFulfillmentType,
            Object.is(item?.isAutoAppliedDiscount, undefined) ||
                item.isAutoAppliedDiscount
                ? []
                : excludeAutoDiscountIds,
            orderChannel,
        ) &&
        appliesToCompareByItemFilterDiscount(item, discount) &&
        itemMinReqFilterDiscount(item, discount)
    )
}

export type TAutoDiscountByItemsMapping = {
    automaticDiscounts: DiscountContract[]
    checkingFulfillmentType: OrderFulfilmentType
    excludeAutoDiscountIds: string[]
    orderChannel: OrderChannel
}
export const autoDiscountByItemsMapping = (
    dependencies: TAutoDiscountByItemsMapping,
) => (items: OrderItemViewModel[]) => {
    const {
        automaticDiscounts: discounts,
        checkingFulfillmentType,
        excludeAutoDiscountIds,
        orderChannel,
    } = dependencies
    return items.map((item) => {
        const { discounts: curItemDiscounts = [] } = item

        /**
         * case: user already added discount by manual or use in some promotion
         * so skip add auto discount
         */
        if (
            curItemDiscounts.find(
                (f) =>
                    !f.isAutomaticDiscount || f.type === DiscountType.BuyXGetY,
            )
        )
            return item

        let autoDiscounts = curItemDiscounts
        if (
            item.isAutoAppliedDiscount ||
            item.isAutoAppliedDiscount === undefined
        ) {
            autoDiscounts = discounts.filter((eachDiscount) =>
                groupAutoEachItemDiscountFilter(
                    eachDiscount,
                    item,
                    checkingFulfillmentType,
                    excludeAutoDiscountIds,
                    orderChannel,
                ),
            )

            autoDiscounts = applyToPriorityOfDiscounts(autoDiscounts)

            autoDiscounts = chooseOptimalAutoDiscount(autoDiscounts, item)
        }

        return {
            ...item,
            discounts: autoDiscounts,
        }
    })
}

export const groupEntireOrderDiscountFilter = (
    discount: DiscountContract,
    items: OrderItemViewModel[],
    checkingFulfillmentType: number,
    excludeAutoDiscountIds: string[] = [],
    orderChannelID: number,
) => {
    if (
        !generalDiscountFilter(
            discount,
            checkingFulfillmentType,
            excludeAutoDiscountIds,
            orderChannelID,
        )
    ) {
        return false
    }

    if (!entireOrderDiscountfilterByMinRequirement(items, discount)) {
        return false
    }

    return true
}

export const autoDiscountByEntireOrderMapping = (
    items: OrderItemViewModel[],
    appliedDiscounts: DiscountContractWithCustom[],
    checkingFulfillmentType: OrderFulfilmentType,
    excludeAutoDiscountIds: string[] = [],
    orderChannelID: number,
): DiscountContract[] => {
    /**
     * if user already added manual Entire discounts, so skip it
     */
    if (appliedDiscounts.some((s) => !s.isAutomaticDiscount)) {
        return appliedDiscounts
    }

    let autoEntireDiscounts = appliedDiscounts.filter(
        (f) => f.level === DiscountLevel.EntireOrder && f.isAutomaticDiscount,
    )

    autoEntireDiscounts = applyToPriorityOfDiscounts(autoEntireDiscounts)

    autoEntireDiscounts = autoEntireDiscounts.filter((eachDiscount) =>
        groupEntireOrderDiscountFilter(
            eachDiscount,
            items,
            checkingFulfillmentType,
            excludeAutoDiscountIds,
            orderChannelID,
        ),
    )

    const subTotal = sumLineSubtotal(items)

    /**
     * check filterDiscounts have 2 isCombinable types ? (combine / non-combine)
     */
    const combineTypeCount = autoEntireDiscounts.filter((f) => f.isCombinable)
        .length

    /**
     * if match both
     * priority to Combinable
     */
    if (combineTypeCount > 0) {
        autoEntireDiscounts = autoEntireDiscounts.filter((f) => f.isCombinable)
        if (combineTypeCount > 1) {
            autoEntireDiscounts = sortDiscountTypeOfDiscount(
                autoEntireDiscounts,
            )
        }
    } else {
        /**
         * Non-Combinable case
         */
        const bestNonCombine = chooseBestNonCombinableDiscount(
            subTotal,
            autoEntireDiscounts,
        )
        autoEntireDiscounts = bestNonCombine ? [bestNonCombine] : []
    }

    return autoEntireDiscounts
}

export const getItemLevelAutomaticDiscounts = (
    discounts: DiscountContract[],
) => {
    return discounts.filter(
        (f) =>
            f.isAutomaticDiscount &&
            f.appliesTo !== DiscountAppliesTo.EntireOrder &&
            (f.type === DiscountType.FixedAmount ||
                f.type === DiscountType.Percentage),
    )
}
